/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import FrameworkUtils from 'utils/framework';
import { Flex } from 'components/ui';
import { mediaQueries } from 'style/config';
import {
    STATUS, EVENT_TYPES, MESSAGE_TYPES, RESPONSE_TYPES
} from 'ai/constants/superchat';
import genAISocketService from 'ai/services/socket/socketService';
import SuperChatHeader from './SuperChatHeader/SuperChatHeader';
import PreSelectedPrompts from './PreSelectedPrompts/PreSelectedPrompts';
import InputBar from './InputBar/InputBar';
import Messages from './Messages/Messages';
import InitialMessages from 'ai/components/SuperChat/InitialMessages/InitialMessages';
import SuperChatFlyout from 'ai/components/SuperChat/SuperChatFlyout/SuperChatFlyout';

import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';

// Util functions
import { setGENAISession } from 'ai/utils/sessionStorage';
import mapPreviousConversations from 'ai/utils/mapPreviousConversations';
import findPreviousSession from 'ai/utils/findPreviousSession';

const { wrapFunctionalComponent } = FrameworkUtils;

function SuperChat(props) {
    const {
        localization,
        entryPointData,
        prompts,
        showSuperChat,
        createSession,
        closeSuperChat,
        config,
        language,
        country,
        clientId,
        fetchPreviousConversations
    } = props;

    if (!showSuperChat) {
        return null;
    }

    const [previousMessages, setPreviousMessages] = useState([]);
    const [messages, setMessages] = useState([]);
    const [sessionId, setSessionId] = useState('');
    const [hiddenContextualChips, setHiddenContextualChips] = useState(new Set());
    const [latestContextualChipIndex, setLatestContextualChipIndex] = useState(-1);
    const [isLoading, setIsLoading] = useState(true);
    const [showFlyer, setShowFlyer] = useState(false);
    const [showFeedbackInMessages, setShowFeedbackInMessages] = useState(false);
    const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);

    useEffect(() => {
        const initialize = async () => {
            try {
                let sessionFound = null;

                if (clientId) {
                    setIsLoadingPrevious(true);
                    const conversationResponse = await fetchPreviousConversations(clientId);
                    const mappedConversationsObj = mapPreviousConversations(conversationResponse);

                    setPreviousMessages(mappedConversationsObj);
                    setIsLoadingPrevious(false);

                    if (mappedConversationsObj.length > 0) {
                        sessionFound = findPreviousSession(conversationResponse, entryPointData);
                    }
                }

                if (entryPointData.sessionId) {
                    sessionFound = entryPointData.sessionId;
                }

                const sessionResponse = await createSession(sessionFound);
                genAISocketService.connect(sessionResponse.token, sessionResponse.genai_pa_session_id, language, country, clientId);
                setSessionId(sessionResponse.genai_pa_session_id);
                // Store the session ID to validate if we have started any conversation, and display the minimized chat button
                setGENAISession({
                    sessionId: sessionResponse.genai_pa_session_id,
                    clientId: clientId,
                    ...entryPointData
                });
                genAISocketService.onMessage(handleSocketMessage);

                setTimeout(() => genAISocketService.sendInitialConnection(entryPointData), 1000);
            } catch (error) {
                Sephora.logger.verbose('SuperChat: Failed to initialize Super Chat:', error);
            }
        };

        initialize();

        return () => {
            genAISocketService.disconnect();
        };
    }, []);

    const handleSocketMessage = event => {
        if (event.type === EVENT_TYPES.CONNECTION) {
            if (event.status === STATUS.DISCONNECTED) {
                handleClose();
            }
        } else {
            // Hide loading when we receive any response
            setIsLoading(false);

            setMessages(prev => {
                const newMessages = [...prev, event];

                // Track the index of the latest contextual chip message
                if (event.response_type === RESPONSE_TYPES.FOLLOW_UP_MESSAGES_RESPONSE) {
                    setLatestContextualChipIndex(newMessages.length - 1);
                    setHiddenContextualChips(new Set()); // Reset hidden chips for new contextual chips
                }

                return newMessages;
            });
        }
    };

    const handleContextualChipClick = (messageIndex, chip) => {
        // Send the message
        sendMessage(chip.content);

        // Hide this contextual chip message
        setHiddenContextualChips(prev => new Set(prev).add(messageIndex));
    };

    const sendAnalytics = () => {
        processEvent.process(anaConsts.LINK_TRACKING_EVENT, {
            data: {
                pageName: `${anaConsts.SUPER_CHAT.SUPERCHAT_LANDING}`,
                actionInfo: `${anaConsts.SUPER_CHAT.SUPERCHAT_EXIT}` // prop55
            }
        });
    };

    const handleClose = () => {
        sendAnalytics();
        genAISocketService.disconnect();
        closeSuperChat();
    };

    const sendMessage = message => {
        if (!message || !message.trim()) {
            return;
        }

        // Send the user query through the socket service
        const messageId = genAISocketService.sendUserQuery(message.trim());

        if (messageId) {
            // Add the user message to the local messages state for immediate UI feedback
            const userMessage = {
                type: EVENT_TYPES.MESSAGE,
                message_type: MESSAGE_TYPES.USER_QUERY,
                data: {
                    query: message.trim()
                }
            };
            setMessages(prev => [...prev, userMessage]);

            // Show loading indicator
            setIsLoading(true);
        }
    };

    const handleShowFeedback = () => {
        // Close the flyer and show feedback in messages
        setShowFlyer(false);
        setShowFeedbackInMessages(true);
    };

    const handleCloseFeedback = () => {
        setShowFeedbackInMessages(false);
    };

    const handleToggleFlyer = () => {
        setShowFlyer(!showFlyer);
    };

    return (
        <>
            <div
                css={[styles.fixed, styles.backdrop]}
                style={{ opacity: 1 }}
            />
            <Flex
                css={styles.box}
                right={[0, 3]}
            >
                <Flex
                    height='100%'
                    position='relative'
                    flexDirection='column'
                    justifyContent='flex-end'
                    alignItems='flex-start'
                    css={styles.container}
                >
                    <SuperChatHeader
                        title={config?.meta?.bot_name || localization.title}
                        handleToggleFlyer={handleToggleFlyer}
                        showFlyer={showFlyer}
                        handleClose={handleClose}
                    />

                    {/* Chat Content Area */}
                    <Flex
                        flex='1'
                        width='100%'
                        backgroundColor='white'
                        flexDirection='column'
                        justifyContent='flex-end'
                        alignItems='flex-start'
                        overflow='hidden'
                    >
                        {/* Messages container - improved for scrolling */}
                        <Flex
                            alignSelf='stretch'
                            flexDirection='column'
                            justifyContent='flex-end'
                            flex='1'
                            width='100%'
                            overflow='hidden'
                        >
                            <Flex
                                padding={4}
                                backgroundColor='white'
                                flexDirection='column'
                                justifyContent='flex-start'
                                alignItems='flex-start'
                                flex='1'
                                overflow='auto'
                                css={{
                                    overflowY: 'auto',
                                    '&::-webkit-scrollbar': { display: 'none' }
                                }}
                            >
                                {/* Messages content */}
                                <Flex
                                    flexDirection='column'
                                    justifyContent='flex-start'
                                    alignItems='flex-start'
                                    gap={5}
                                    alignSelf='stretch'
                                    width='100%'
                                    marginBottom={4}
                                >
                                    {/* Previous Messages will be here */}
                                    {previousMessages.length > 0 && (
                                        <Messages
                                            messages={previousMessages}
                                            sessionId={sessionId}
                                            sendMessage={sendMessage}
                                            hiddenContextualChips={hiddenContextualChips}
                                            onContextualChipClick={handleContextualChipClick}
                                            latestContextualChipIndex={latestContextualChipIndex}
                                            isLoading={false}
                                            isPrevious={true}
                                            showFeedbackInMessages={showFeedbackInMessages}
                                            feedbackSteps={config?.feedback_steps}
                                            onCloseFeedback={handleCloseFeedback}
                                        />
                                    )}

                                    {prompts?.length > 0 && <PreSelectedPrompts prompts={prompts} />}
                                    {!isLoadingPrevious && previousMessages.length === 0 && config && <InitialMessages config={config} />}

                                    {/* New Messages */}
                                    <Messages
                                        messages={messages}
                                        sessionId={sessionId}
                                        sendMessage={sendMessage}
                                        hiddenContextualChips={hiddenContextualChips}
                                        onContextualChipClick={handleContextualChipClick}
                                        latestContextualChipIndex={latestContextualChipIndex}
                                        isLoading={isLoading}
                                        showFeedbackInMessages={showFeedbackInMessages}
                                        feedbackSteps={config?.feedback_steps}
                                        onCloseFeedback={handleCloseFeedback}
                                    />
                                </Flex>
                            </Flex>
                            {/* Input area - kept outside the scrollable region */}
                            <InputBar
                                sendMessage={sendMessage}
                                placeholder={localization.placeholder}
                            />
                        </Flex>
                    </Flex>

                    {/* Full-page Share Feedback Overlay */}
                    <SuperChatFlyout
                        isOpen={showFlyer}
                        onShowFeedback={handleShowFeedback}
                    />
                </Flex>
            </Flex>
        </>
    );
}

const styles = {
    box: {
        position: 'fixed',
        right: 0,
        bottom: 0,
        width: '100%',
        maxWidth: 420,
        maxHeight: 720,
        height: '100%',
        zIndex: 100000000,
        [mediaQueries.smMax]: {
            paddingTop: 98
        }
    },
    container: {
        boxShadow: '0px 0px 12px 0px rgba(0, 0, 0, 0.2)',
        borderTopLeft: 4,
        borderTopRight: 4,
        width: '100%',
        borderTopLeftRadius: 6,
        borderTopRightRadius: 6
    },
    backdrop: {
        backgroundColor: 'rgba(0,0,0,.25)',
        transition: 'opacity 300ms',
        display: 'none',
        [mediaQueries.smMax]: {
            display: 'block'
        }
    },
    fixed: {
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--layer-modal)'
    }
};

export default wrapFunctionalComponent(SuperChat, 'SuperChat');
